// ----------------------------------------------------------------------
// Dato CMS Translation Plugin
//
// Dato plugin sidebar React component
// ----------------------------------------------------------------------
// Sidebar.tsx
// Oeil Pour Oeil - 2025

import React, { useState } from "react";
import { RenderItemFormSidebarPanelCtx } from "datocms-plugin-sdk";
import { Locales } from "../types";

import {
  Button,
  Form,
  SelectField,
  SwitchField,
  FieldGroup,
  Spinner,
} from "datocms-react-ui";
//import { JsxElement } from 'typescript';
import langs from "../langs.json";
import { Translator } from "../translator";
import s from "../styles.module.css";
import "async";

type InitTranslator = () => Promise<Translator>;

const findLangByValue = (value: string) =>
  langs.find((lang) => lang.value === value);
const findLangsByValues = (values: string[]) =>
  values.flatMap((value) => findLangByValue(value));

export default function Sidebar({
  locales,
  initTranslator,
  ctx,
}: {
  locales: Locales;
  initTranslator: InitTranslator;
  ctx: RenderItemFormSidebarPanelCtx;
}) {
  const [source, setSource] = useState(locales.main);
  const [target, setTarget] = useState(locales.current);
  const [extraLanguages, setExtraLanguages] = useState<string[]>(locales.extra);

  const [override, setOverride] = useState(false);
  const [useChatGPT, setUseChatGPT] = useState(false);
  const [batchTranslations, setBatchTranslations] = useState(false);
  const [translateAnswers, setTranslateAnswers] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const removeSourceAndCurrent = (values: string[]) =>
    values.filter((e) => e !== locales.main && e !== locales.current);

  // --- Events Handling --

  const handleSubmit = async () => {
    setError(false);
    setLoading(true);

    await initTranslator().then((translator) =>
      translator(
        {
          source,
          target,
          extraLanguages,
          override,
          useChatGPT,
          batchTranslations,
          translateAnswers,
        },
        (err) => {
          setLoading(false);
          if (!err) {
            ctx.notice(
              "Translation from '" +
                source +
                "' to '" +
                target +
                "' finished without error",
            );
          } else {
            setError(true);
            const message =
              "Translation from '" +
              source +
              "' to '" +
              target +
              "' finished with '" +
              err.name +
              "'\r" +
              err.message;
            ctx.alert(message);
            setErrMsg(message);
          }
        },
      ),
    );
  };

  const handleChange = (newValues: any) => {
    var selectedLocales = (newValues as any[]).map((e) => e.value);
    var validLocales = removeSourceAndCurrent(selectedLocales);
    setExtraLanguages(validLocales);
  };

  // --- View Builder --

  const translateSwitch  = ""
  /*
  const translateSwitch  = "
          <SwitchField
          name="translateAnswers"
          id="translateAnswers"
          label="Translate Answers"
          hint="If active, the answers associated to the question will be translated too."
          value={translateAnswers}
          onChange={(value) => setTranslateAnswers(value)}
        />
  "
  */
  return (
    <Form>
      <FieldGroup>
        <SelectField
          required
          name="source"
          id="source"
          label="Source Language"
          onChange={(value) => value !== null && setSource(value.value)}
          value={findLangByValue(source)}
          selectInputProps={{
            isMulti: false,
            options: langs,
          }}
        />
        <SelectField
          required
          name="target"
          id="target"
          label="Target Language"
          onChange={(value) => value !== null && setTarget(value.value)}
          value={findLangByValue(target)}
          selectInputProps={{
            isMulti: false,
            options: langs,
          }}
        />
        <SwitchField
          name="override"
          id="override"
          label="Override target content"
          hint="If active, texts will be replaced by the translated version even if they are already defined. In the other case, only missing texts will receive the translation."
          value={override}
          onChange={(value) => setOverride(value)}
        />
        <SwitchField
          name="useChatGPT"
          id="useChatGPT"
          label="Use ChatGPT"
          hint="Default translator is DeepL. Activating ChatGPT may offer better translations in some cases."
          value={useChatGPT}
          onChange={(value) => setUseChatGPT(value)}
        />
        <SwitchField
          name="batch"
          id="batch"
          label="Batch"
          hint="If active, the translations will be done in all languages selected below, ignoring the first 'Target Language' selector."
          value={batchTranslations}
          onChange={(value) => setBatchTranslations(value)}
        />
        {translateSwitch}
        <SelectField
          name="extraLanguages"
          id="extraLanguages"
          label="Extra Languages"
          onChange={(value) => value !== null && handleChange(value)}
          value={findLangsByValues(extraLanguages)}
          selectInputProps={{
            isMulti: true,
            options: langs,
          }}
        />
      </FieldGroup>
      <FieldGroup>
        {error && <p className={s.error}>{errMsg}</p>}
        {loading ? (
          <Spinner />
        ) : (
          <Button
            fullWidth
            buttonType="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            Translate
          </Button>
        )}
      </FieldGroup>
    </Form>
  );
}
